import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Package, Tag, Store, DollarSign, ListChecks, Pencil } from 'lucide-react'
import { productsService } from '@/services/productsService'
import { categoriesService } from '@/services/categoriesService'
import { ProductFormDialog } from './ProductsPage'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import StatusBadge from '@/components/shared/StatusBadge'
import { formatCurrency, getLocalizedField } from '@/utils/formatters'

// Maps the full product-detail response into the flat shape ProductFormDialog's
// form state expects (same shape ProductsPage.jsx builds in openEdit/the ?edit= deep link).
function buildEditInitialData(p) {
  return {
    id: p.id,
    vendorId: p.vendor?.id || '',
    categoryId: p.category?.id || '',
    nameAr: p.name?.ar || '',
    nameEn: p.name?.en || '',
    descAr: p.description?.ar || '',
    descEn: p.description?.en || '',
    metaTitleAr: p.metaTitle?.ar || '',
    metaTitleEn: p.metaTitle?.en || '',
    metaDescAr: p.metaDescription?.ar || '',
    metaDescEn: p.metaDescription?.en || '',
    price: p.price || '',
    comparePrice: p.comparePrice || '',
    dealEndsAt: p.dealEndsAt ? new Date(p.dealEndsAt).toISOString().slice(0, 16) : '',
    shippingClassId: p.shippingClassId || p.shippingClass?.id || '',
    sku: p.sku || '',
    stockQuantity: p.stockQuantity ?? '',
    lowStockThreshold: p.lowStockThreshold || 5,
    status: p.status || 'active',
    tags: (p.tags || []).map((t) => (typeof t === 'string' ? t : t.tag)).join(', '),
    variants: p.variants || [],
    images: (p.images || []).map((img, i) => ({
      url: img.url,
      altText: img.altText || '',
      isPrimary: img.isPrimary ?? i === 0,
      sortOrder: img.sortOrder ?? i,
    })),
    attributes: (p.attributes || []).map((a) => ({ key: a.definition?.key ?? a.key, value: a.value })),
  }
}

export default function ProductDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ['admin-product', id],
    queryFn: () => productsService.getById(id),
  })

  // Categories are only needed once editing starts (the picker inside the form).
  const { data: catTree } = useQuery({
    queryKey: ['categories-tree'],
    queryFn: categoriesService.getTree,
    enabled: editing,
  })
  const flatCats = []
  const flatten = (arr, depth = 0) => {
    arr?.forEach((c) => {
      flatCats.push({ id: c.id, name: (depth > 0 ? '— ' : '') + (c.name?.[lang] || c.name?.ar || c.name?.en || '') })
      if (c.children?.length) flatten(c.children, depth + 1)
    })
  }
  flatten(catTree)

  if (isLoading) return <LoadingSpinner />
  if (isError || !product) {
    return (
      <div className="p-6 text-center text-red-500 font-bold font-tajawal">
        حدث خطأ أثناء تحميل تفاصيل المنتج
      </div>
    )
  }

  const name = getLocalizedField(product.name, lang)
  const desc = getLocalizedField(product.description, lang)
  const vendorName = getLocalizedField(product.vendor?.storeName, lang) || 'غير معروف'
  const catName = getLocalizedField(product.category?.name, lang) || 'بدون فئة'

  // Primary image
  const primaryImg = product.images?.find(img => img.isPrimary)?.url || product.images?.[0]?.url

  return (
    <div className="space-y-6">
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4">
        <button onClick={() => (editing ? setEditing(false) : navigate(-1))} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
          <ArrowRight size={24} className="text-navy" />
        </button>
        <div>
          <h1 className="font-black text-navy text-2xl" style={{ fontFamily: 'Cairo, sans-serif' }}>
            {name}
          </h1>
          <p className="text-sm text-ghost mt-1" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            تفاصيل المنتج (ID: {product.id.slice(0, 8)}...)
          </p>
        </div>
        <div className="mr-auto flex items-center gap-3">
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-colors"
              style={{ background: '#000666', fontFamily: 'Cairo, sans-serif' }}
            >
              <Pencil size={16} />
              تعديل
            </button>
          )}
          <StatusBadge status={product.status} type="product" lang={lang} />
        </div>
      </div>

      {editing ? (
        <ProductFormDialog
          mode="edit"
          initialData={buildEditInitialData(product)}
          flatCats={flatCats}
          onClose={() => setEditing(false)}
          onSuccess={() => {
            setEditing(false)
            qc.invalidateQueries({ queryKey: ['admin-product', id] })
          }}
          t={t}
          lang={lang}
          inline
        />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Right Column: Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-[#F3F3F3] shadow-sm">
            <h2 className="text-lg font-bold text-navy mb-4 border-b pb-3 border-[#F3F3F3]" style={{ fontFamily: 'Cairo, sans-serif' }}>
              المعلومات الأساسية
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
              <div>
                <span className="block text-xs font-bold text-ghost uppercase mb-1">الاسم بالإنجليزية</span>
                <span className="text-sm font-medium text-ink">{product.name?.en || '—'}</span>
              </div>
              <div>
                <span className="block text-xs font-bold text-ghost uppercase mb-1">الاسم بالعربية</span>
                <span className="text-sm font-medium text-ink">{product.name?.ar || '—'}</span>
              </div>

              <div className="md:col-span-2">
                <span className="block text-xs font-bold text-ghost uppercase mb-1">الوصف بالإنجليزية</span>
                {product.description?.en ? (
                  <div className="text-sm text-ink whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: product.description.en }} />
                ) : (
                  <p className="text-sm text-ink">—</p>
                )}
              </div>
              <div className="md:col-span-2">
                <span className="block text-xs font-bold text-ghost uppercase mb-1">الوصف بالعربية</span>
                {product.description?.ar ? (
                  <div className="text-sm text-ink whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: product.description.ar }} />
                ) : (
                  <p className="text-sm text-ink">—</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-[#F3F3F3] shadow-sm">
            <h2 className="text-lg font-bold text-navy mb-4 border-b pb-3 border-[#F3F3F3]" style={{ fontFamily: 'Cairo, sans-serif' }}>
              السعر والمخزون
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#F8F9FA] p-4 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-green-600">
                  <DollarSign size={20} />
                </div>
                <div>
                  <span className="block text-xs font-bold text-ghost mb-1">السعر الحالي</span>
                  <span className="text-lg font-black text-navy">{formatCurrency(product.price)}</span>
                </div>
              </div>

              <div className="bg-[#F8F9FA] p-4 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-gray-400">
                  <DollarSign size={20} />
                </div>
                <div>
                  <span className="block text-xs font-bold text-ghost mb-1">السعر القديم</span>
                  <span className="text-sm font-bold text-ink line-through">
                    {product.comparePrice ? formatCurrency(product.comparePrice) : '—'}
                  </span>
                </div>
              </div>

              <div className="bg-[#F8F9FA] p-4 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-blue-600">
                  <Package size={20} />
                </div>
                <div>
                  <span className="block text-xs font-bold text-ghost mb-1">المخزون المتوفر</span>
                  <span className="text-lg font-black text-navy">{product.stockQuantity || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {product.attributes && product.attributes.length > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-[#F3F3F3] shadow-sm">
              <h2 className="text-lg font-bold text-navy mb-4 border-b pb-3 border-[#F3F3F3] flex items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
                <ListChecks size={18} />
                المواصفات (Attributes)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {product.attributes.map((attr) => (
                  <div key={attr.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <span className="text-sm font-bold text-ghost">{getLocalizedField(attr.definition?.label, lang) || attr.definition?.key}</span>
                    <span className="text-sm font-black text-navy">{attr.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.variants && product.variants.length > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-[#F3F3F3] shadow-sm">
              <h2 className="text-lg font-bold text-navy mb-4 border-b pb-3 border-[#F3F3F3] flex items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
                <Package size={18} />
                المقاسات والمتغيرات ({product.variants.length})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-right" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                  <thead className="text-xs text-ghost border-b border-gray-100">
                    <tr>
                      <th className="py-2 font-bold">الاسم</th>
                      <th className="py-2 font-bold">السعر</th>
                      <th className="py-2 font-bold">المخزون</th>
                      <th className="py-2 font-bold">SKU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.variants.map((v) => (
                      <tr key={v.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-2 text-ink font-bold">{v.name}</td>
                        <td className="py-2 text-orange-600 font-bold">{formatCurrency(v.price)}</td>
                        <td className="py-2 text-navy">{v.stockQuantity}</td>
                        <td className="py-2 text-gray-500" dir="ltr">{v.sku || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Left Column: Media & Meta */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-[#F3F3F3] shadow-sm">
            <h2 className="text-lg font-bold text-navy mb-4 border-b pb-3 border-[#F3F3F3]" style={{ fontFamily: 'Cairo, sans-serif' }}>
              صورة المنتج
            </h2>
            <div className="aspect-square bg-gray-50 rounded-xl border border-gray-100 overflow-hidden flex items-center justify-center mb-4">
              {primaryImg ? (
                <img src={primaryImg} alt={name} className="w-full h-full object-contain" />
              ) : (
                <Package size={48} className="text-gray-300" />
              )}
            </div>

            {product.images && product.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.map(img => (
                  <img key={img.id} src={img.url} alt="img" className={`w-16 h-16 rounded-lg object-cover border ${img.isPrimary ? 'border-primary' : 'border-gray-200'}`} />
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-[#F3F3F3] shadow-sm space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
                <Store size={16} />
              </div>
              <div>
                <span className="block text-xs text-ghost">البائع</span>
                <span className="font-bold text-sm text-navy">{vendorName}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center">
                <Tag size={16} />
              </div>
              <div>
                <span className="block text-xs text-ghost">الفئة</span>
                <span className="font-bold text-sm text-navy">{catName}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center">
                <Package size={16} />
              </div>
              <div>
                <span className="block text-xs text-ghost">SKU</span>
                <span className="font-bold text-sm text-navy">{product.sku || '—'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  )
}
